// Configuration for asset detail fields and sections
import { getAllCountries } from '@/utils/country-utils';

export interface HiddenCondition {
  key: string;
  value: string;
}

export interface AssetFieldConfig {
  key: string;
  label: string;
  type?: 'text' | 'select' | 'tags' | 'date' | 'controlcombobox' | 'url' | 'datatypecombobox' | 'legalobligations' | 'riskscombobox';
  options?: string[] | Array<{key: string; value: string}>;
  section: string;
  fullWidth?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  hidden?: HiddenCondition;
  description?: string;
  showSecurityScore?: boolean;
  /** Array of plan names for which this field should be hidden (e.g., ['starter', 'Starter', 'Starter Plan']) */
  hiddenForPlans?: string[];
}

// Static field config that will be merged with dynamic user options
export const staticAssetFieldsConfig: AssetFieldConfig[] = [
  // Basic Information
  { key: 'name', label: 'Name', section: 'Basic Information', type: 'text' },
  { key: 'status', label: 'Status', section: 'Basic Information', type: 'select', options: ['Active', 'Decommissioned', 'New Inactive'] },
  { key: 'type', label: 'Type', section: 'Basic Information', type: 'select', options: ['Asset', 'Supplier'] },
  { key: 'nextReviewDate', label: 'Next Review Date', section: 'Basic Information', type: 'date' },
  { key: 'lastReviewDate', label: 'Last Review Date', section: 'Basic Information', type: 'date' },
  { key: 'category', label: 'Category', section: 'Basic Information', type: 'select', options: ['People', 'Infrastructure', 'IT Hardware', 'Software', 'Documentation', 'Outsourced Services', 'Installed Software'] },
  { key: 'owner', label: 'Owner', section: 'Basic Information', type: 'select', options: [] }, // options will be populated dynamically

  // Risk & Compliance
  { key: 'criticality', label: 'Criticality', section: 'Risk & Compliance', type: 'select', options: ['Low', 'Medium', 'High'] },
  { key: 'risk', label: 'Risk', section: 'Risk & Compliance', type: 'select', options: ['Low', 'Medium', 'High'] },
  { key: 'dataClassification', label: 'Data Classification', section: 'Risk & Compliance', type: 'select', options: ['Customer Confidential', 'Business Confidential', 'Public'] },
  // DED-1447: Hide Legal Obligations for Starter tenancies
  { key: 'legalObligations', label: 'Legal Obligations', section: 'Risk & Compliance', type: 'legalobligations', hiddenForPlans: ['starter', 'Starter', 'Starter Plan'] },
  // DED-1447: Hide Subprocessor for Starter tenancies
  { key: 'subprocessor', label: 'Subprocessor', section: 'Risk & Compliance', type: 'select', options: ['Yes', 'No'], hiddenForPlans: ['starter', 'Starter', 'Starter Plan'] },
  { key: 'publishInPrivacyPolicy', label: 'Publish in Privacy Policy', section: 'Risk & Compliance', type: 'select', options: ['Yes', 'No'] },
  { key: 'annualSecurityReviewCompleted', label: 'Annual Security Review Completed', section: 'Risk & Compliance', type: 'select', options: ['Yes', 'No'] },
  { key: 'usesAI', label: 'Uses AI', section: 'Risk & Compliance', type: 'select', options: ['Yes', 'No'] },
  { key: 'vendorManagementNeeded', label: 'Vendor Monitoring', section: 'Risk & Compliance', type: 'select', options: ['Yes', 'No'] },
  // DED-1447: Hide Related Risks for Starter tenancies
  { key: 'risks', label: 'Related Risks', section: 'Risk & Compliance', type: 'riskscombobox', fullWidth: true, hiddenForPlans: ['starter', 'Starter', 'Starter Plan'] },
  { 
    key: 'vendorWebsite', 
    label: 'Vendor Website', 
    section: 'Risk & Compliance', 
    type: 'url', 
    hidden: { key: 'vendorManagementNeeded', value: 'No' },
    description: 'Enter the vendor website URL to check security score',
    showSecurityScore: true
  },
  { key: 'relatedControls', label: 'Related Controls', section: 'Risk & Compliance', type: 'controlcombobox', fullWidth: false },
  // Data Information
  { key: 'dataTypesShared', label: 'Data Types Shared', section: 'Data Information', type: 'datatypecombobox', fullWidth: true },
  { key: 'dataStorageLocations', label: 'Data Storage Locations', section: 'Data Information', type: 'tags', options: getAllCountries(), fullWidth: true },

  // Additional Fields
  { key: 'portable', label: 'Portable', section: 'Additional Fields', type: 'select', options: ['Yes', 'No'] },
  { key: 'serialNumber', label: 'Serial Number', section: 'Additional Fields', type: 'text' },
  { key: 'label', label: 'Physical Label', section: 'Additional Fields', type: 'select', options: ['Yes', 'No'] },
  { key: 'physicalLabel', label: 'Label', section: 'Additional Fields', type: 'text', hidden: { key: 'label', value: 'No' } },
  { key: 'socCriticality', label: 'SOC Criticality', section: 'Additional Fields', type: 'select', options: ['N/A', 'Not Critical', 'Critical'] },
  { key: 'backupsRequired', label: 'Backups Required', section: 'Additional Fields', type: 'select', options: ['Yes', 'No'] },
  // { key: 'componentId', label: 'Component ID', section: 'Additional Fields', type: 'text', disabled: true },

  // Asset Metadata (non-editable fields)
  { key: 'componentId', label: 'Component ID', section: 'Asset Metadata', type: 'text', readOnly: true },
  { key: 'dateAdded', label: 'Date Added', section: 'Asset Metadata', type: 'date', readOnly: true },

  // Custom Fields (handled dynamically)
  // { key: 'customField1', label: 'Custom Field 1', section: 'Custom Fields', type: 'text' },
];

// Generate field config with dynamic user options
// DED-1447: Added customerPlan parameter to filter out fields based on plan
export function generateAssetFieldsConfig(users: any[] = [], customerPlan?: string): AssetFieldConfig[] {
  let config = [...staticAssetFieldsConfig];
  
  // DED-1447: Filter out fields that should be hidden for the current plan
  if (customerPlan) {
    config = config.filter(field => {
      if (!field.hiddenForPlans) return true;
      return !field.hiddenForPlans.includes(customerPlan);
    });
  }
  
  // Find the owner field and update its options with user data
  const ownerFieldIndex = config.findIndex(field => field.key === 'owner');
  
  if (ownerFieldIndex !== -1 && users.length > 0) {
    config[ownerFieldIndex] = {
      ...config[ownerFieldIndex],
      options: users.map((user) => ({
        key: `${user.firstName || ''} ${user.lastName || ''} ${user.email ? `<${user.email}>` : ''}`.trim(),
        value: user.id
      }))
    };
  }
  
  return config;
}
